import { Plus } from 'lucide-react';
import { useState } from 'react';
import styled, { css } from 'styled-components';

import { Flex } from '@/components/base';
import Button from '@/components/Button/Button';
import FlexibleTextarea from '@/components/Inputs/FlexibleTextarea';
import Tooltip from '@/components/Tooltip/TooltipV2';
import Typo from '@/components/Typography/Typo';
import keymap from '@/utils/keymap';

type Props = {
  desc?: string;
  onDoneClick: (desc?: string) => void;
};

const DescriptionEditor = (props: Props) => {
  const [editMode, setEditMode] = useState(false);
  const [value, setValue] = useState(props.desc);

  if (editMode)
    return (
      <FlexibleTextarea
        autoFocus
        css={css`
          background-color: ${(props) => props.theme.colors.gray100};
          min-height: 80px;
          &:hover {
            background-color: ${(props) => props.theme.colors.gray100};
            border: 1px solid ${(props) => props.theme.colors.gray400};
          }
          &:focus {
            background-color: ${(props) => props.theme.colors.gray100};
          }
        `}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Type description..."
        onBlur={async () => {
          await props.onDoneClick(value);
          setEditMode(false);
        }}
        onKeyDown={(e) => {
          if (keymap.isEnter(e)) {
            if (!e.shiftKey) {
              e.currentTarget.blur();
            }
          }
        }}
      />
    );

  return value ? (
    <Tooltip
      tooltipNode={
        <span
          dangerouslySetInnerHTML={{ __html: value.replaceAll('\n', '<br/>') }}
        />
      }
    >
      <Style g={12} onClick={() => setEditMode(true)}>
        <Typo
          type="body2"
          weight="r"
          className="text-gray-700"
          multilineEllipsis={2}
        >
          {value}
        </Typo>
      </Style>
    </Tooltip>
  ) : (
    <Flex padding="8px 12px">
      <Button
        variant="ghost_secondary"
        edgeAlign="left"
        leftIcon={<Plus />}
        sizeType="s"
        onClick={() => setEditMode(true)}
      >
        Add Description
      </Button>
    </Flex>
  );
};

const Style = styled(Flex)`
  border: 1px solid transparent;
  padding: 12px;
  border-radius: ${(props) => props.theme.radius.token.sm}px;
  transition: all 0.2s;
  min-height: 40px;
  cursor: text;
  &:hover {
    background-color: ${(props) => props.theme.colors.gray200};
    border-color: ${(props) => props.theme.colors.gray400};
  }
`;

export default DescriptionEditor;
